import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWhatInput } from "../hooks/useWhatInput";
import { useCallbackRef } from "../hooks/useCallbackRef";
import { MotionState } from "./types";
export const CLOSE_INTENT = 200;
export const usePopperState = ({ isOpenProp, delay, onClose, onClosed, onOpen, onOpened, onOpenCallback, onCloseCallback, shouldCloseOnScroll }) => {
    const frameRef = useRef();
    const isTouch = useWhatInput().inputType === "touch";
    const [motionState, setMotionState] = useState(MotionState.Closed);
    // eslint-disable-next-line react/hook-use-state
    const [isOpen, setIsOpenState] = useState(isOpenProp || false);
    const isControlled = isOpenProp !== undefined;
    // Do nothing on open if controlled
    const setIsOpen = useMemo(() => (isControlled ? () => { } : setIsOpenState), [isControlled]);
    const isOpenRef = useRef(isOpen);
    const isInitialRenderRef = useRef(true);
    // Call callbacks on isOpen change (but not when onOpen/onClose callbacks change)
    const callbackRef = useCallbackRef({ onClose, onClosed, onOpen, onOpened, onCloseCallback, onOpenCallback });
    useEffect(() => {
        if (isInitialRenderRef.current) {
            isInitialRenderRef.current = false;
            return;
        }
        const { onClose, onClosed, onOpen, onOpened } = callbackRef.current;
        if (process.env.NODE_ENV === "test") {
            // animation states are skipped and only closed/opened are used in tests
            switch (motionState) {
                case MotionState.Closed:
                case MotionState.Closing:
                    onClose?.();
                    onClosed?.();
                    break;
                case MotionState.Opening:
                case MotionState.Opened:
                    onOpen?.();
                    onOpened?.();
                    break;
                default:
                    break;
            }
            return;
        }
        switch (motionState) {
            case MotionState.Opening:
                onOpen?.();
                break;
            case MotionState.Closing:
                onClose?.();
                break;
            case MotionState.Closed:
                onClosed?.();
                break;
            case MotionState.Opened:
                onOpened?.();
                break;
            default:
                break;
        }
    }, [callbackRef, motionState]);
    // Stored for use in toggle callback so isOpen change doesn't invalidate
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);
    useEffect(() => {
        // Use isOpenProp for controlled mode if defined
        if (isOpenProp === undefined) {
            return;
        }
        setIsOpenState(isOpenProp);
    }, [isOpenProp]);
    const clearClose = useCallback(() => {
        window.clearTimeout(frameRef.current);
    }, []);
    const close = useCallback((e) => {
        window.clearTimeout(frameRef.current);
        if (isOpenRef.current === false) {
            return;
        }
        if (!isControlled) {
            isOpenRef.current = false;
            setIsOpen(isOpenRef.current);
        }
        // close when called without an event is from the controlled isOpen prop changing
        // in which case, you don't want to call the callback again. Only call this in response to an event listener
        const { onCloseCallback } = callbackRef.current;
        e && onCloseCallback?.(e);
    }, [setIsOpen, callbackRef, isControlled]);
    const open = useCallback((e) => {
        window.clearTimeout(frameRef.current);
        if (isOpenRef.current === true) {
            return;
        }
        if (!isControlled) {
            isOpenRef.current = true;
            setIsOpen(isOpenRef.current);
        }
        const { onOpenCallback } = callbackRef.current;
        e && onOpenCallback?.(e);
    }, [setIsOpen, callbackRef, isControlled]);
    const toggle = useCallback((e) => {
        isOpenRef.current ? close(e) : open(e);
    }, [close, open]);
    const closeOnIntent = useCallback(() => {
        if (process.env.NODE_ENV === "test") {
            close();
            return;
        }
        window.clearTimeout(frameRef.current);
        frameRef.current = window.setTimeout(close, CLOSE_INTENT);
    }, [close]);
    const openOnDelay = useCallback(() => {
        if (process.env.NODE_ENV === "test") {
            open();
            return;
        }
        window.clearTimeout(frameRef.current);
        frameRef.current = window.setTimeout(open, delay);
    }, [delay, open]);
    // Use isOpenProp for controlled mode if defined
    useEffect(() => {
        if (isOpenProp === undefined) {
            return;
        }
        isOpenProp ? open() : close();
    }, [close, isOpenProp, open, setIsOpen]);
    useEffect(() => {
        if (!shouldCloseOnScroll) {
            return;
        }
        window.addEventListener("scroll", close, { once: true });
        return () => {
            window.removeEventListener("scroll", close);
        };
    }, [shouldCloseOnScroll, close]);
    return {
        close,
        clearClose,
        closeOnIntent,
        isOpen,
        isTouch,
        openOnDelay,
        setIsOpen,
        motionState,
        setMotionState,
        toggle
    };
};
